import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "./utils"
import { Elevated } from "./elevated"

/**
 * A TOOLTIP IS NOT A PLACE TO PUT YOUR MOUSE (Matt, 2026-07-22: "hovering over
 * the tooltip should hide it, not maintain it").
 *
 * Radix's default is a HOVERABLE bubble: it keeps a grace polygon between the
 * trigger and the content so the pointer can travel into it, and it stays open
 * the whole time the pointer is inside. That is the right default for a bubble
 * you can interact with — ours never are, they hold one line of text — so all it
 * bought here was a label that lingers over the very content it is floating
 * above, and only leaves when you steer around it.
 *
 * Two switches, because either alone leaves half the behavior:
 *   · `disableHoverableContent` drops the grace polygon, so moving toward the
 *     bubble counts as leaving the trigger and it closes;
 *   · `pointer-events-none` on the content (below) makes the bubble untouchable
 *     even when it lands directly under the cursor's path — without it the
 *     content still swallows the pointer, and the element beneath it stops
 *     receiving hover.
 */
function TooltipProvider({
 delayDuration = 0,
 disableHoverableContent = true,
 ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
 return (
 <TooltipPrimitive.Provider
 data-slot="tooltip-provider"
 delayDuration={delayDuration}
 disableHoverableContent={disableHoverableContent}
 {...props}
 />
 )
}

function Tooltip({
 ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
 return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({
 ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
 return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
 className,
 sideOffset = 0,
 children,
 ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
 return (
 <TooltipPrimitive.Portal>
 <Elevated offset={2} shadowLevel={3} asChild>
 <TooltipPrimitive.Content
 data-slot="tooltip-content"
 sideOffset={sideOffset}
 className={cn(
 // The bubble is a label, never a target. See the note on TooltipProvider
 // — this is the half of "hovering it hides it" that
 // `disableHoverableContent` can't do on its own, and it also keeps the
 // 150ms fade-out from eating a click aimed at whatever is underneath.
 //
 // `!` because INSIDE A DIALOG this loses without it: Radix's
 // DismissableLayer writes `pointer-events: auto` as an INLINE style when
 // body pointer-events are disabled (which is exactly what an open Dialog
 // does), and inline beats a plain class. Measured in the peek: computed
 // `auto` with the bare utility.
 //
 // z-[90], not z-50: this portals to document.body, so it is a SIBLING
 // of Dialog/Sheet in the root stacking context, not a child. At z-50 it
 // merely TIED with Dialog and won on portal mount order; once Dialog
 // moved to z-[80] every tooltip inside a modal went invisible —
 // including PeekDialog's, reachable anywhere via ?peek=<uid>. Keep in
 // lockstep with the Z-LEDGER in dialog.tsx.
 "pointer-events-none! text-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[90] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 type-subtext text-balance",
 className
 )}
 {...props}
 >
 {children}
 </TooltipPrimitive.Content>
 </Elevated>
 </TooltipPrimitive.Portal>
 )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
